import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover, Input } from 'antd';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import InsertLinkIcon from '@material-ui/icons/InsertLink';
import { countVisibleChars, linkifyUrls, normalizeDivLineBreaks } from './simple-html-editor-utils';
import './SimpleHtmlEditor.css';

export interface SimpleHtmlEditorProps {
    value: string;
    onChange: (html: string) => void;
    /** Max visible characters (tags excluded). Defaults to 500. */
    maxLength?: number;
    disabled?: boolean;
    placeholder?: string;
    warningMessage?: string;
}

// ─── Module-level DOM helpers (no complexity contribution to the component) ──

function makeLink(url: string): HTMLAnchorElement {
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
    link.textContent = url;
    return link;
}

function hardenLinks(editor: HTMLElement, url: string): void {
    editor.querySelectorAll<HTMLAnchorElement>('a').forEach((a) => {
        if (a.getAttribute('href') === url) {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
        }
    });
}

function insertOrWrapLink(url: string, sel: Selection, saved: Range): HTMLAnchorElement | null {
    sel.removeAllRanges();
    sel.addRange(saved);
    if (saved.collapsed) {
        const link = makeLink(url);
        saved.insertNode(link);
        return link;
    }
    const start = sel.rangeCount > 0 ? sel.getRangeAt(0).startContainer : null;
    document.execCommand('createLink', false, url);
    const el = start?.nodeType === Node.ELEMENT_NODE ? (start as HTMLElement) : start?.parentElement ?? null;
    return el?.closest('a') ?? null;
}

function resolveLink(url: string, sel: Selection | null, saved: Range | null, editor: HTMLElement): HTMLAnchorElement | null {
    if (sel && saved && editor.contains(saved.commonAncestorContainer)) {
        return insertOrWrapLink(url, sel, saved);
    }
    const link = makeLink(url);
    editor.appendChild(link);
    return link;
}

function placeCaretAfter(node: Node, editor: HTMLElement): void {
    const sel = window.getSelection();
    if (!sel) return;
    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    editor.focus();
}

function finalizeInsertion(inserted: HTMLAnchorElement | null, editor: HTMLElement): void {
    if (inserted && editor.contains(inserted)) {
        placeCaretAfter(inserted, editor);
    }
}

function captureSelection(editor: HTMLDivElement | null): Range | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    return editor?.contains(range.commonAncestorContainer) ? range.cloneRange() : null;
}

function syncDomIfExternal(editor: HTMLDivElement | null, value: string, lastReported: string): void {
    if (!editor || value === lastReported) return;
    if (editor.innerHTML !== value) editor.innerHTML = value ?? '';
}

function isInListItem(editor: HTMLDivElement | null, anchorNode: Node | null): boolean {
    let node = anchorNode;
    while (node && node !== editor) {
        if (node.nodeName === 'LI') return true;
        node = node.parentNode;
    }
    return false;
}

function tryCaptureSavedSelection(editor: HTMLDivElement | null, ref: { current: Range | null }): void {
    const range = captureSelection(editor);
    if (range) ref.current = range;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

interface TextBtnProps {
    label: string;
    command: string;
    title: string;
    style: React.CSSProperties;
    active?: boolean;
    onExec: (cmd: string) => void;
}

const TextBtn: React.FC<TextBtnProps> = ({ label, command, title, style, active, onExec }) => (
    <button type="button" className={`she-btn${active ? ' she-btn--active' : ''}`} title={title} style={style}
        onMouseDown={(e) => { e.preventDefault(); onExec(command); }}>
        {label}
    </button>
);

interface IconBtnProps {
    Icon: React.ComponentType<{ fontSize?: 'small' }>;
    command: string;
    title: string;
    active?: boolean;
    onExec: (cmd: string) => void;
}

const IconBtn: React.FC<IconBtnProps> = ({ Icon, command, title, active, onExec }) => (
    <button type="button" className={`she-btn${active ? ' she-btn--active' : ''}`} title={title}
        onMouseDown={(e) => { e.preventDefault(); onExec(command); }}>
        <Icon fontSize="small" />
    </button>
);

interface LinkPopoverProps {
    open: boolean;
    url: string;
    onOpenChange: (open: boolean) => void;
    onUrlChange: (url: string) => void;
    onApply: () => void;
    onSaveSelection: () => void;
}

const LinkPopover: React.FC<LinkPopoverProps> = ({ open, url, onOpenChange, onUrlChange, onApply, onSaveSelection }) => (
    <Popover
        trigger="click"
        open={open}
        onOpenChange={onOpenChange}
        content={
            <div style={{ display: 'flex', gap: '8px' }}>
                <Input autoFocus placeholder="https://" value={url}
                    onChange={(e) => onUrlChange(e.target.value)}
                    onPressEnter={onApply}
                    style={{ width: '240px' }} />
                <button type="button" className="btn btn-success" onClick={onApply} style={{ height: '40px' }}>OK</button>
            </div>
        }
    >
        <button type="button" className="she-btn" title="Insert link"
            onMouseDown={(e) => { e.preventDefault(); onSaveSelection(); }}>
            <InsertLinkIcon fontSize="small" />
        </button>
    </Popover>
);

// ─── Main component ───────────────────────────────────────────────────────────

const SimpleHtmlEditor: React.FC<SimpleHtmlEditorProps> = ({
    value,
    onChange,
    maxLength = 500,
    disabled = false,
    placeholder,
    warningMessage,
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [linkOpen, setLinkOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [activeCommands, setActiveCommands] = useState<Record<string, boolean>>({});
    const savedSelection = useRef<Range | null>(null);

    const refreshActiveCommands = useCallback(() => {
        const cmds = ['bold', 'italic', 'underline', 'strikeThrough', 'insertUnorderedList', 'insertOrderedList'];
        const next: Record<string, boolean> = {};
        cmds.forEach(cmd => { next[cmd] = document.queryCommandState(cmd); });
        setActiveCommands(next);
    }, []);
    // Tracks the last HTML we reported to the parent so the useEffect can tell
    // whether a value change originated from user input (skip DOM reset) or an
    // external prop change like a form clear (apply DOM reset).
    const lastReported = useRef<string>('');

    useEffect(() => { syncDomIfExternal(editorRef.current, value, lastReported.current); }, [value]);

    const syncChange = () => {
        const html = normalizeDivLineBreaks(editorRef.current?.innerHTML ?? '');
        lastReported.current = html;
        onChange(html);
    };

    const exec = (command: string) => {
        editorRef.current?.focus();
        document.execCommand(command, false);
        syncChange();
        refreshActiveCommands();
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        document.execCommand('insertHTML', false, linkifyUrls(e.clipboardData.getData('text/plain')));
        syncChange();
    };

    const saveSelection = () => tryCaptureSavedSelection(editorRef.current, savedSelection);

    // Override Enter outside lists to insert <br> instead of Chrome's <div>.
    // <br> is in ALLOWED_TAGS; <div> is not, so it would be stripped on render.
    // List items are left to the browser (it adds new <li> correctly).
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== 'Enter' || isInListItem(editorRef.current, window.getSelection()?.anchorNode ?? null)) return;
        e.preventDefault();
        document.execCommand('insertLineBreak');
    };

    const applyLink = () => {
        const url = linkUrl.trim();
        setLinkOpen(false);
        setLinkUrl('');
        const editor = editorRef.current;
        if (!url || !editor) return;
        editor.focus();
        const inserted = resolveLink(url, window.getSelection(), savedSelection.current, editor);
        hardenLinks(editor, url);
        syncChange();
        savedSelection.current = null;
        finalizeInsertion(inserted, editor);
    };

    const atLimit = countVisibleChars(value) > maxLength;

    return (
        <div>
            <div className={`she-container${disabled ? ' she-container--disabled' : ''}`}>
                {!disabled && (
                    <div className="she-toolbar">
                        <div className="she-group">
                            <TextBtn label="B" command="bold" title="Bold" style={{ fontWeight: 700 }} active={activeCommands['bold']} onExec={exec} />
                            <TextBtn label="I" command="italic" title="Italic" style={{ fontStyle: 'italic' }} active={activeCommands['italic']} onExec={exec} />
                            <TextBtn label="U" command="underline" title="Underline" style={{ textDecoration: 'underline' }} active={activeCommands['underline']} onExec={exec} />
                            <TextBtn label="S" command="strikeThrough" title="Strikethrough" style={{ textDecoration: 'line-through' }} active={activeCommands['strikeThrough']} onExec={exec} />
                        </div>
                        <div className="she-separator" />
                        <div className="she-group">
                            <IconBtn Icon={FormatListBulletedIcon} command="insertUnorderedList" title="Bullet list" active={activeCommands['insertUnorderedList']} onExec={exec} />
                            <IconBtn Icon={FormatListNumberedIcon} command="insertOrderedList" title="Numbered list" active={activeCommands['insertOrderedList']} onExec={exec} />
                        </div>
                        <div className="she-separator" />
                        <div className="she-group">
                            <LinkPopover
                                open={linkOpen}
                                url={linkUrl}
                                onOpenChange={setLinkOpen}
                                onUrlChange={setLinkUrl}
                                onApply={applyLink}
                                onSaveSelection={saveSelection}
                            />
                        </div>
                    </div>
                )}
                <div
                    ref={editorRef}
                    className="she-editor"
                    contentEditable={!disabled}
                    suppressContentEditableWarning
                    data-placeholder={placeholder}
                    onKeyDown={handleKeyDown}
                    onKeyUp={refreshActiveCommands}
                    onMouseUp={refreshActiveCommands}
                    onSelect={refreshActiveCommands}
                    onInput={syncChange}
                    onPaste={handlePaste}
                    onBlur={syncChange}
                />
            </div>
            {atLimit && (
                <div className="she-warning">
                    {warningMessage ?? `กรุณาระบุคำอธิบาย Banner ให้ไม่เกิน ${maxLength} ตัวอักษร`}
                </div>
            )}
        </div>
    );
};

export default SimpleHtmlEditor;
